import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, UserCog, FolderTree, Hammer, ArrowUpRight } from 'lucide-react';

/**
 * DashboardBreakdownTabs — tabbed widget showing the count of jobs grouped by
 * Service Head / Job Type / Supervisor / Technician. Each value is a clickable
 * tile that navigates to /service/jobs?<key>=<value> (multi-select pre-filtered).
 */
const TABS = [
  { id: 'service_head', label: 'Service Head', Icon: FolderTree, accent: 'sky' },
  { id: 'job_type',     label: 'Job Type',     Icon: Hammer,     accent: 'violet' },
  { id: 'supervisor',   label: 'Supervisor',   Icon: UserCog,    accent: 'amber' },
  { id: 'technician',   label: 'Technician',   Icon: Wrench,     accent: 'emerald' },
];

const accentBar = {
  sky: 'from-sky-500 to-indigo-500',
  violet: 'from-violet-500 to-fuchsia-500',
  amber: 'from-amber-500 to-orange-500',
  emerald: 'from-emerald-500 to-teal-500',
};

const accentText = {
  sky: 'text-sky-700 bg-sky-50',
  violet: 'text-violet-700 bg-violet-50',
  amber: 'text-amber-700 bg-amber-50',
  emerald: 'text-emerald-700 bg-emerald-50',
};

export function DashboardBreakdownTabs({ jobs, dateFrom, dateTo }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('service_head');

  const data = React.useMemo(() => {
    const counts = {};
    jobs.forEach((j) => {
      const key = (j[activeTab] || '').toString().trim();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [jobs, activeTab]);

  const handleTileClick = (value) => {
    const params = new URLSearchParams();
    params.set(activeTab, value);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    navigate(`/service/jobs?${params.toString()}`);
  };

  const activeMeta = TABS.find((t) => t.id === activeTab);

  return (
    <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="dashboard-breakdown">
      {/* Tab strip */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 overflow-x-auto">
        {TABS.map((t) => {
          const isActive = t.id === activeTab;
          const TIcon = t.Icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`breakdown-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium whitespace-nowrap transition-all inline-flex items-center gap-1.5
                ${isActive
                  ? 'bg-sky-600 text-white shadow-sm shadow-sky-500/20'
                  : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'}`}
            >
              <TIcon size={13} /> {t.label}
            </button>
          );
        })}
        <span className="flex-1" />
        <span className="text-[11.5px] text-slate-400 px-2">
          {data.length} unique value{data.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Body — clickable tiles */}
      <div className="p-3">
        {data.length === 0 ? (
          <p className="py-10 text-center text-[13px] text-slate-400">
            No {activeMeta?.label.toLowerCase()} data for the selected range.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
            {data.map((d) => (
              <button
                key={d.name}
                onClick={() => handleTileClick(d.name)}
                data-testid={`breakdown-tile-${activeTab}-${d.name}`}
                className="group relative rounded-xl bg-white/80 backdrop-blur border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all p-3 text-left overflow-hidden"
              >
                <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${accentBar[activeMeta.accent]}`} />
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-[12px] font-medium text-slate-800 line-clamp-2 leading-tight" title={d.name}>{d.name}</p>
                  <ArrowUpRight size={12} className="text-slate-300 group-hover:text-sky-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[20px] font-semibold tracking-tight text-slate-900 leading-none">{d.value}</span>
                  <span className={`text-[10.5px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${accentText[activeMeta.accent]}`}>
                    job{d.value === 1 ? '' : 's'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardBreakdownTabs;
